<template>
  <box>
    <template #header>
      <h2 class="header-title">{{ t('Data de Entrega') }}</h2>
    </template>

    <div class="container">
      <div class="columns-layout" :class="{ 'has-history': history.length > 0 }">
        <!-- Left column: delivery dates -->
        <div class="delivery-date-column">
          <div class="current-date-row" data-testid="delivery-date-original-row">
            <label class="label-current">{{ t('Data estimada original') }}:</label>
            <span v-if="originalDateFormatted" class="date-value" data-testid="delivery-date-original">{{ originalDateFormatted }}</span>
            <span v-else class="date-value-empty">{{ t('Não definida') }}</span>
          </div>

          <div class="current-date-row" data-testid="delivery-date-current-row">
            <label class="label-current">{{ t('Data estimada atual') }}:</label>
            <span v-if="currentDateFormatted" class="date-value" data-testid="delivery-date-current">{{ currentDateFormatted }}</span>
            <span v-else class="date-value-empty">{{ t('Não definida') }}</span>
          </div>

          <div v-if="!readOnly" class="change-date-button-wrapper">
            <Button
              :label="t('Alterar data de entrega')"
              icon="pi pi-calendar"
              size="small"
              data-testid="open-delivery-date-change-btn"
              @click="openChangeModal"
            />
          </div>
        </div>

        <!-- Right column: history timeline -->
        <div v-if="history.length > 0" class="history-column">
          <h3 class="history-title">{{ t('Histórico de alterações') }}</h3>
          <div class="history-timeline">
            <div
              v-for="entry in history"
              :key="entry.so_oidh_id"
              class="history-entry"
              :data-testid="`delivery-date-history-row-${entry.so_oidh_id}`"
            >
              <div class="entry-timestamp">
                {{ formatDateTime(new Date(entry.so_oidh_changed_at)) }}
                <span v-if="entry.so_oidh_changed_by_name">
                  — {{ entry.so_oidh_changed_by_name }}
                </span>
              </div>
              <div class="entry-dates">
                <span v-if="entry.so_oidh_previous_date" class="date-previous">
                  {{ formatDisplayDate(entry.so_oidh_previous_date) }}
                </span>
                <span v-if="entry.so_oidh_previous_date" class="date-arrow">→</span>
                <span class="date-new">{{ formatDisplayDate(entry.so_oidh_new_date) }}</span>
              </div>
              <div class="entry-reason">
                {{ entry.so_oidh_reason }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </box>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSODeliveryDate } from '../../composables/useSODeliveryDate';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { eventBus } from '../../eventBus';
import type { Orders_DeliveryDateHistoryRow } from '../../contracts/Orders_DeliveryDateHistoryRow';
import type { Orders_UpdateItemDeliveryDateResponseDados } from '../../contracts/Orders_UpdateItemDeliveryDateResponse';

const { t } = useI18n();
const { formatDateTime, formatDate } = useDateFormatter();
const { fetchHistory } = useSODeliveryDate();

interface Props {
  orderItemId: number;
  currentExpectedDeliveryAt?: Date | null;
  readOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  currentExpectedDeliveryAt: null,
  readOnly: false,
});

const emit = defineEmits<{
  updated: [itemId: number];
}>();

const history = ref<Orders_DeliveryDateHistoryRow[]>([]);
const currentDateFormatted = ref('');

function formatDisplayDate(dateStr: string): string {
  const d = new Date(dateStr);
  return formatDate(d);
}

// Original date = previous_date of the oldest history entry (the row whose
// "new_date" became the second-ever date). history is sorted newest-first by
// the backend, so the oldest entry is the last one. If there's no history at
// all, the current date IS the original (never changed).
const originalDateFormatted = computed<string>(() => {
  if (history.value.length > 0) {
    const oldest = history.value[history.value.length - 1];
    if (oldest?.so_oidh_previous_date) {
      return formatDisplayDate(oldest.so_oidh_previous_date);
    }
    // First change had no previous_date (item created without a date) →
    // fall through to current.
  }
  return currentDateFormatted.value;
});

onMounted(() => {
  loadData();
  eventBus.on('so-order-item.delivery-date.updated', handleDeliveryDateUpdated);
});

onBeforeUnmount(() => {
  eventBus.off('so-order-item.delivery-date.updated', handleDeliveryDateUpdated);
});

async function loadData() {
  try {
    const result = await fetchHistory(props.orderItemId);
    if (result?.history) {
      history.value = result.history;
    }

    if (props.currentExpectedDeliveryAt) {
      currentDateFormatted.value = formatDate(props.currentExpectedDeliveryAt);
    }
  } catch (err) {
    // Error handled by composable
  }
}

function openChangeModal(): void {
  eventBus.emit('modal.so-delivery-date-change.open', { itemId: props.orderItemId });
}

interface DeliveryDateUpdatedPayload {
  itemId: number;
  result: Orders_UpdateItemDeliveryDateResponseDados;
}

function handleDeliveryDateUpdated(payload?: DeliveryDateUpdatedPayload): void {
  if (!payload || payload.itemId !== props.orderItemId) return;

  const result = payload.result;
  if (result?.history_entry) {
    history.value.unshift(result.history_entry);
  }
  if (result?.expected_delivery_at) {
    currentDateFormatted.value = formatDate(new Date(result.expected_delivery_at));
  }
  emit('updated', props.orderItemId);
}
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.header-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.current-date-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.label-current {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--p-text-color);
}

.date-value {
  font-size: 0.875rem;
  color: var(--p-text-color);
}

.date-value-empty {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.change-date-button-wrapper {
  display: flex;
}

.history-column {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.columns-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .columns-layout.has-history {
    grid-template-columns: 1fr 1fr;
  }
}

.delivery-date-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.history-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.history-timeline {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 16rem;
  overflow-y: auto;
}

.history-entry {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  border-left: 2px solid var(--p-primary-color);
  padding-left: 0.75rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

.history-entry:not(:last-child) {
  border-bottom: 1px solid var(--p-surface-border);
  padding-bottom: 0.5rem;
}

.entry-timestamp {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
}

.entry-dates {
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.date-previous {
  text-decoration: line-through;
  color: var(--p-text-muted-color);
}

.date-arrow {
  color: var(--p-text-muted-color);
}

.date-new {
  font-weight: 500;
  color: var(--p-text-color);
}

.entry-reason {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  font-style: italic;
}
</style>
